import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/locales';

interface RouteError {
    status?: number;
    statusText?: string;
    message?: string;
    data?: any;
}

export default function RouteErrorBoundary() {
    const error = useRouteError() as RouteError;
    const navigate = useNavigate();
    const { t } = useTranslation();

    // 根据错误类型显示不同的信息
    const getErrorInfo = () => {
        if (error?.status === 404) {
            return {
                title: t('error.route.404.title', '页面未找到'),
                description: t('error.route.404.description', '抱歉，您访问的页面不存在。'),
                icon: (
                    <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                )
            };
        }

        if (error?.status === 403) {
            return {
                title: t('error.route.403.title', '访问被拒绝'),
                description: t('error.route.403.description', '您没有权限访问此页面。'),
                icon: (
                    <svg className="h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                )
            };
        }

        if (error?.status === 500) {
            return {
                title: t('error.route.500.title', '服务器错误'),
                description: t('error.route.500.description', '服务器遇到了一个错误，请稍后再试。'),
                icon: (
                    <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )
            };
        }

        // 默认错误信息
        return {
            title: t('error.route.default.title', '页面加载失败'),
            description: error?.message || error?.statusText || t('error.route.default.description', '页面加载时遇到了一个错误。'),
            icon: (
                <svg className="h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
            )
        };
    };

    const errorInfo = getErrorInfo();

    const handleGoBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleReload = () => {
        window.location.reload();
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex justify-center">
                    {errorInfo.icon}
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
                    {errorInfo.title}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    {errorInfo.description}
                </p>
                
                {/* 开发环境显示错误详情 */}
                {process.env.NODE_ENV === 'development' && error && (
                    <details className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow border">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('error.route.details', '错误详情')}
                        </summary>
                        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                            {error.status && (
                                <div><strong>状态码:</strong> {error.status}</div>
                            )}
                            {error.statusText && (
                                <div><strong>状态文本:</strong> {error.statusText}</div>
                            )}
                            {error.message && (
                                <div><strong>错误信息:</strong> {error.message}</div>
                            )}
                            {error.data && (
                                <div>
                                    <strong>错误数据:</strong>
                                    <pre className="mt-1 whitespace-pre-wrap break-words">
                                        {JSON.stringify(error.data, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </details>
                )}
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="space-y-4">
                        <button
                            onClick={handleReload}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            {t('error.route.reload', '重新加载')}
                        </button>
                        
                        <button
                            onClick={handleGoBack}
                            className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            {t('error.route.goBack', '返回上页')}
                        </button>
                        
                        <button
                            onClick={handleGoHome}
                            className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            {t('error.route.goHome', '返回首页')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}